/**
 * SaveQueue - Central save queue for all editor operations
 *
 * Key features:
 * - Coalesces rapid saves to same block (debounce by blockId)
 * - Exponential backoff retry (1s, 3s, 9s)
 * - 409 conflict handling (no retry, surface to ConflictResolver)
 * - 423 locked handling (Phase 4: no retry, surface for UI notification)
 * - Offline awareness via ConnectionMonitor
 *
 * Implementation: Task 1.0
 *
 * Usage:
 *   const saveQueue = new SaveQueue({
 *     updateUrl: '/admin/websites/:id/update_block_content',
 *     csrfToken: document.querySelector('meta[name="csrf-token"]').content,
 *     connectionMonitor: connectionMonitorInstance,
 *     onStatusChange: (status, item, extra) => { ... }
 *   })
 *
 *   saveQueue.enqueue(blockId, { headline: 'New text' }, '5', 'inline')
 */

import { trackEditorEvent, EditorEvents } from './editor_analytics.js'
import { createLogger } from './logger.js'

// Phase 5: Shared logger with debug gating
const logger = createLogger('SaveQueue')

/**
 * QueueItem represents a single save operation
 * @typedef {Object} QueueItem
 * @property {string} blockId - The block being saved
 * @property {Object} patches - Content patches to save (merged from multiple rapid edits)
 * @property {string|null} expectedLockVersion - For optimistic locking
 * @property {string} source - Where the edit came from ('inline', 'side_panel', 'array_editor', etc.)
 * @property {number} retryCount - Number of retries attempted
 * @property {number} createdAt - Timestamp when first enqueued
 * @property {number} updatedAt - Timestamp of last patch merge
 */

// Default configuration
const DEFAULT_OPTIONS = {
  coalesceDelay: 500, // ms to wait before processing (allows merging rapid edits)
  maxRetries: 3, // Maximum retry attempts
  retryDelays: [1000, 3000, 9000], // Exponential backoff delays
  maxQueueSize: 100, // Phase 10: Maximum pending items to prevent memory growth
  updateUrl: null, // Required: endpoint URL
  csrfToken: null, // Required: CSRF token
  connectionMonitor: null, // Optional: ConnectionMonitor instance
  onStatusChange: null, // Optional: callback for status updates
  enableBeforeUnload: true // Phase 10: Warn user before leaving with unsaved changes
}

// Queue item statuses
export const QueueStatus = {
  PENDING: 'pending', // Waiting to be processed (may be coalescing)
  PROCESSING: 'processing', // Currently being sent to server
  RETRYING: 'retrying', // Failed, waiting for retry
  CONFLICT: 'conflict', // 409 conflict - needs user resolution
  LOCKED: 'locked', // ADDED: Phase 4 - 423 locked by another user (no retry)
  OFFLINE_QUEUED: 'offline_queued', // Queued while offline
  COMPLETED: 'completed', // Successfully saved
  FAILED: 'failed' // Failed after max retries
}

export class SaveQueue {
  /**
   * @param {Object} options - Configuration options
   * @param {string} options.updateUrl - Required: PATCH endpoint URL
   * @param {string} options.csrfToken - Required: CSRF token for requests
   * @param {Object} options.connectionMonitor - Optional: ConnectionMonitor instance
   * @param {Function} options.onStatusChange - Optional: (status, item, extra) => void
   * @param {number} options.coalesceDelay - Optional: ms to wait before processing (default 500)
   * @param {number} options.maxRetries - Optional: max retry attempts (default 3)
   * @param {number[]} options.retryDelays - Optional: retry delay array (default [1000, 3000, 9000])
   */
  constructor(options = {}) {
    this.options = { ...DEFAULT_OPTIONS, ...options }

    // Validate required options
    if (!this.options.updateUrl) {
      console.error('[SaveQueue] updateUrl is required')
    }
    if (!this.options.csrfToken) {
      console.error('[SaveQueue] csrfToken is required')
    }

    // Queue state
    // Map<blockId, QueueItem> - pending items waiting to be processed
    this.queue = new Map()

    // Array of QueueItems currently being processed (sent to server)
    this.processingQueue = []

    // Map<blockId, timeoutId> - coalesce timers for debouncing rapid edits
    this.coalesceTimers = new Map()

    // Map<blockId, timeoutId> - retry timers for failed saves
    this.retryTimers = new Map()

    // Map<blockId, {resolve, reject}[]> - promises waiting for save completion (P0-4 fix)
    this.pendingPromises = new Map()

    // P0-5 FIX: Map<blockId, QueueItem> - blocked items (423 locked) preserved for retry
    this.blockedChanges = new Map()

    // ADDED: Set of status change listeners for multi-subscriber support (Phase 2 Task 0.1.1)
    this.statusListeners = new Set()

    // Processing state
    this.isProcessing = false
    this.isPaused = false // Paused when offline

    // ADDED: If legacy onStatusChange callback provided, register it as first listener (backward compat)
    if (typeof this.options.onStatusChange === 'function') {
      this.statusListeners.add(this.options.onStatusChange)
    }

    // Subscribe to connection monitor if provided
    if (this.options.connectionMonitor) {
      this.options.connectionMonitor.subscribe((isOnline) => {
        this.handleConnectionChange(isOnline)
      })
    }

    // Phase 10: Set up beforeunload warning for unsaved changes
    this.handleBeforeUnload = this.handleBeforeUnload.bind(this)
    if (this.options.enableBeforeUnload) {
      window.addEventListener('beforeunload', this.handleBeforeUnload)
    }

    logger.log('Initialized with options:', {
      updateUrl: this.options.updateUrl,
      coalesceDelay: this.options.coalesceDelay,
      maxRetries: this.options.maxRetries,
      hasConnectionMonitor: !!this.options.connectionMonitor
    })
  }

  /**
   * Enqueue a save operation for a block
   * Coalesces rapid edits to the same block within coalesceDelay window
   *
   * @param {string} blockId - Block ID to save
   * @param {Object} patches - Content patches (will be merged with existing pending patches)
   * @param {string|null|undefined} expectedLockVersion - Lock version for conflict detection (null forces no lock_version)
   * @param {string} source - Source of the edit ('inline', 'side_panel', 'array_editor', etc.)
   */
  enqueue(blockId, patches, expectedLockVersion = undefined, source = 'unknown') {
    // Task 1.2 will implement full coalescing logic
    logger.log('enqueue() called:', { blockId, patches, expectedLockVersion, source })

    const now = Date.now()
    const existingItem = this.queue.get(blockId)

    if (existingItem) {
      // Merge patches into existing item
      existingItem.patches = { ...existingItem.patches, ...patches }
      existingItem.updatedAt = now
      // Keep the original lock version (from first edit)
      logger.log('Merged patches for block:', blockId)
    } else {
      // Phase 10: Enforce queue size limit to prevent memory growth
      if (this.queue.size >= this.options.maxQueueSize) {
        // Phase 10 FIX: Properly clean up dropped item - resolve promises, clear timer, notify
        const oldestKey = this.queue.keys().next().value
        const droppedItem = this.queue.get(oldestKey)

        logger.warn('Queue size limit reached, dropping oldest item:', oldestKey)

        // Clear coalesce timer for the dropped block
        if (this.coalesceTimers.has(oldestKey)) {
          clearTimeout(this.coalesceTimers.get(oldestKey))
          this.coalesceTimers.delete(oldestKey)
        }

        // Reject any pending promises for the dropped block
        this.rejectPendingPromises(oldestKey, new Error('Save dropped: queue size limit reached'))

        // Notify status change so UI can warn user
        this.notifyStatusChange(QueueStatus.FAILED, droppedItem, {
          error: 'queue_overflow',
          message: 'Edit dropped due to queue overflow. Please try again.'
        })

        this.queue.delete(oldestKey)
      }

      // Create new queue item
      const item = {
        blockId,
        patches,
        expectedLockVersion: expectedLockVersion === undefined ? this.resolveLockVersion(blockId) : expectedLockVersion,
        source,
        retryCount: 0,
        createdAt: now,
        updatedAt: now
      }
      this.queue.set(blockId, item)
      logger.log('Added new item for block:', blockId)
    }

    // Notify status change
    this.notifyStatusChange(QueueStatus.PENDING, this.queue.get(blockId))

    // Clear existing coalesce timer and set new one
    this.resetCoalesceTimer(blockId)
  }

  /**
   * Enqueue a save and return a promise that resolves when the save completes
   * Use this when you need to wait for save completion (e.g., before page reload)
   *
   * @param {string} blockId - Block ID to save
   * @param {Object} patches - Content patches
   * @param {string|null} expectedLockVersion - Lock version for conflict detection
   * @param {string} source - Source of the edit
   * @returns {Promise<{success: boolean, result?: Object, error?: string}>}
   */
  enqueueAndWait(blockId, patches, expectedLockVersion = undefined, source = 'unknown') {
    return new Promise((resolve, reject) => {
      // Add promise callbacks to pending list for this block
      if (!this.pendingPromises.has(blockId)) {
        this.pendingPromises.set(blockId, [])
      }
      this.pendingPromises.get(blockId).push({ resolve, reject })

      // Enqueue the save
      this.enqueue(blockId, patches, expectedLockVersion, source)
    })
  }

  /**
   * Resolve all pending promises for a block
   * @private
   */
  resolvePendingPromises(blockId, success, data) {
    const promises = this.pendingPromises.get(blockId) || []
    for (const { resolve } of promises) {
      resolve({ success, ...data })
    }
    this.pendingPromises.delete(blockId)
  }

  /**
   * Reject all pending promises for a block
   * @private
   */
  rejectPendingPromises(blockId, error) {
    const promises = this.pendingPromises.get(blockId) || []
    for (const { reject } of promises) {
      reject(error)
    }
    this.pendingPromises.delete(blockId)
  }

  /**
   * Reset the coalesce timer for a block
   * Timer triggers processQueue() after coalesceDelay
   * @private
   */
  resetCoalesceTimer(blockId) {
    // Clear existing timer
    if (this.coalesceTimers.has(blockId)) {
      clearTimeout(this.coalesceTimers.get(blockId))
    }

    // Set new timer
    const timerId = setTimeout(() => {
      this.coalesceTimers.delete(blockId)
      this.processQueue()
    }, this.options.coalesceDelay)

    this.coalesceTimers.set(blockId, timerId)
  }

  /**
   * Process pending items in the queue
   * Called automatically after coalesce timer or manually
   * @private
   */
  async processQueue() {
    // Task 1.3 will implement full processing with retry logic
    logger.log('processQueue() called')

    // Don't process if paused (offline) or already processing
    if (this.isPaused) {
      logger.log('Paused (offline) - skipping processQueue')
      return
    }

    if (this.isProcessing) {
      logger.log('Already processing - will re-run when done')
      return
    }

    // No items to process
    if (this.queue.size === 0) {
      return
    }

    this.isProcessing = true

    // Move items from queue to processingQueue
    const itemsToProcess = Array.from(this.queue.values())
    this.queue.clear()

    for (const item of itemsToProcess) {
      this.processingQueue.push(item)
      this.notifyStatusChange(QueueStatus.PROCESSING, item)
    }

    // Process each item
    for (const item of [...this.processingQueue]) {
      await this.executeSave(item)
    }

    this.isProcessing = false

    // If more items were added during processing, process them
    if (this.queue.size > 0) {
      this.processQueue()
    }
  }

  /**
   * Execute a single save operation
   * @private
   */
  async executeSave(item) {
    // Task 1.4 will implement full fetch logic
    logger.log('executeSave() for block:', item.blockId)

    trackEditorEvent(EditorEvents.SAVE_STARTED, {
      blockId: item.blockId,
      source: item.source,
      retryCount: item.retryCount
    })

    try {
      const response = await fetch(this.options.updateUrl, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.options.csrfToken,
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          block_id: item.blockId,
          content: item.patches,
          expected_lock_version: item.expectedLockVersion
        })
      })

      const result = await response.json()

      // P0-5 Fix: Any HTTP response means connectivity is working
      // Server responded = connection is good, regardless of status code
      // ConnectionMonitor will separately detect 401/403 as auth issues
      if (this.options.connectionMonitor) {
        this.options.connectionMonitor.recordFetchResult(true, response.status)
      }

      if (response.status === 409) {
        // Conflict - don't retry, surface to ConflictResolver
        this.handleConflict(item, result)
      } else if (response.status === 423) {
        // ADDED: Phase 4 - Locked by another user - don't retry
        this.handleLocked(item, result)
      } else if (!response.ok) {
        // Other error - may retry
        this.handleError(item, result, response.status)
      } else {
        // Success
        this.handleSuccess(item, result)
      }
    } catch (error) {
      // Network error - likely offline
      if (this.options.connectionMonitor) {
        this.options.connectionMonitor.recordFetchResult(false)
      }
      this.handleNetworkError(item, error)
    }
  }

  /**
   * Handle successful save
   * @private
   */
  handleSuccess(item, result) {
    logger.log('Save succeeded for block:', item.blockId, result)

    // Remove from processing queue
    const idx = this.processingQueue.indexOf(item)
    if (idx > -1) {
      this.processingQueue.splice(idx, 1)
    }

    // Update DOM lock_version (Task 1.6)
    this.updateDomLockVersion(item.blockId, result.lock_version)

    // Track analytics
    trackEditorEvent(EditorEvents.SAVE_SUCCEEDED, {
      blockId: item.blockId,
      source: item.source,
      version: result.version,
      lockVersion: result.lock_version,
      skipped: result.skipped
    })

    // Resolve any waiting promises (P0-4 fix)
    this.resolvePendingPromises(item.blockId, true, { result })

    // Notify status change
    this.notifyStatusChange(QueueStatus.COMPLETED, item, { result })
  }

  /**
   * Handle 409 conflict response
   * @private
   */
  async handleConflict(item, result) {
    logger.warn('Conflict for block:', item.blockId, result)

    // Remove from processing queue
    const idx = this.processingQueue.indexOf(item)
    if (idx > -1) {
      this.processingQueue.splice(idx, 1)
    }

    // Update DOM lock_version to server's current version immediately
    // This ensures draft recovery reads the correct lock_version
    this.updateDomLockVersion(item.blockId, result.current_lock_version)

    // Track analytics
    trackEditorEvent(EditorEvents.CONFLICT_DETECTED, {
      blockId: item.blockId,
      source: item.source,
      serverVersion: result.current_version,
      serverLockVersion: result.current_lock_version
    })

    // Notify status change with conflict data
    this.notifyStatusChange(QueueStatus.CONFLICT, item, {
      localContent: item.patches,
      serverContent: result.current_content,
      serverVersion: result.current_version,
      serverLockVersion: result.current_lock_version,
      editedBy: result.edited_by,
      editedAt: result.edited_at,
      message: result.message
    })

    // If conflict resolver is available, prompt user for resolution
    if (this.options.conflictResolver) {
      try {
        const resolution = await this.options.conflictResolver.resolve(
          item.patches,
          result.current_content,
          {
            blockId: item.blockId,
            editedBy: result.edited_by,
            editedAt: result.edited_at,
            message: result.message
          }
        )

        if (resolution.action === 'keep_mine') {
          // Re-enqueue with null lock version to force overwrite
          logger.log('User chose keep_mine, re-enqueueing with force')
          this.enqueue(item.blockId, item.patches, null, `${item.source}_conflict_override`)
        } else {
          // User chose to use server version - update DOM lock version
          logger.log('User chose use_theirs, updating DOM')
          this.updateDomLockVersion(item.blockId, result.current_lock_version)
          // Resolve any waiting promises so callers (e.g., toolbar) can continue
          this.resolvePendingPromises(item.blockId, true, {
            result: { skipped: true, message: 'Used server version' }
          })
          // Notify that conflict was resolved by accepting server version
          this.notifyStatusChange(QueueStatus.COMPLETED, item, {
            result: { skipped: true, message: 'Used server version' }
          })
        }
      } catch (err) {
        console.error('[SaveQueue] Conflict resolution error:', err)
        // On error, preserve local changes by re-enqueueing
        this.enqueue(item.blockId, item.patches, null, `${item.source}_conflict_error`)
      }
    } else {
      // No resolver available; resolve waiters with failure so UI doesn't hang
      this.resolvePendingPromises(item.blockId, false, { error: 'conflict' })
    }
  }

  /**
   * ADDED: Phase 4 - Handle 423 Locked response
   * Block is locked by another admin - do NOT retry immediately, preserve changes for later retry
   * P0-5 FIX: Now preserves blocked changes instead of discarding them
   * @private
   */
  handleLocked(item, result) {
    logger.warn('Block locked by another user:', item.blockId, result)

    // Remove from processing queue
    const idx = this.processingQueue.indexOf(item)
    if (idx > -1) {
      this.processingQueue.splice(idx, 1)
    }

    // Parse lock holder info from response
    const lockedBy = result.locked_by || {}
    const expiresAt = result.expires_at
    const timeRemaining = result.time_remaining

    // P0-5 FIX: Preserve the blocked item for later retry (merge with existing if present)
    const existing = this.blockedChanges.get(item.blockId)
    if (existing) {
      // Merge patches into existing blocked item
      existing.patches = { ...existing.patches, ...item.patches }
      existing.updatedAt = Date.now()
    } else {
      // Store new blocked item
      this.blockedChanges.set(item.blockId, {
        ...item,
        blockedAt: Date.now(),
        blockedBy: lockedBy,
        blockedUntil: expiresAt
      })
    }
    logger.log('Preserved blocked changes for block:', item.blockId, 'total blocked:', this.blockedChanges.size)

    // Track analytics
    trackEditorEvent(EditorEvents.SAVE_FAILED, {
      blockId: item.blockId,
      source: item.source,
      error: 'locked',
      lockedById: lockedBy.id,
      lockedByName: lockedBy.name
    })

    // Resolve waiting promises with locked status (not rejected - allows graceful UI handling)
    this.resolvePendingPromises(item.blockId, false, {
      error: 'locked',
      lockedBy,
      expiresAt,
      timeRemaining,
      preserved: true // P0-5: Indicate changes were preserved
    })

    // Notify status change with lock holder details for UI notification
    this.notifyStatusChange(QueueStatus.LOCKED, item, {
      lockedBy,
      expiresAt,
      timeRemaining,
      preserved: true, // P0-5: Indicate changes were preserved
      message: `This block is currently being edited by ${lockedBy.name || 'another user'}. Your changes have been preserved.`
    })
  }

  /**
   * Handle non-conflict error response
   * @private
   */
  handleError(item, result, statusCode) {
    console.error('[SaveQueue] Error for block:', item.blockId, statusCode, result)

    // Check if should retry
    if (item.retryCount < this.options.maxRetries) {
      this.scheduleRetry(item)
    } else {
      // Max retries exceeded
      this.handleMaxRetriesExceeded(item, result)
    }
  }

  /**
   * Handle network error (likely offline)
   * @private
   */
  handleNetworkError(item, error) {
    console.error('[SaveQueue] Network error for block:', item.blockId, error)

    // Move back to queue for retry when online
    const idx = this.processingQueue.indexOf(item)
    if (idx > -1) {
      this.processingQueue.splice(idx, 1)
    }

    // Re-queue the item
    this.queue.set(item.blockId, item)

    // Notify as offline queued
    this.notifyStatusChange(QueueStatus.OFFLINE_QUEUED, item, { error: error.message })
  }

  /**
   * Schedule a retry for a failed item
   * @private
   */
  scheduleRetry(item) {
    const retryDelay = this.options.retryDelays[item.retryCount] || this.options.retryDelays[this.options.retryDelays.length - 1]
    item.retryCount++

    logger.log('Scheduling retry', item.retryCount, 'for block:', item.blockId, 'in', retryDelay, 'ms')

    // Track analytics
    trackEditorEvent(EditorEvents.SAVE_RETRIED, {
      blockId: item.blockId,
      source: item.source,
      retryCount: item.retryCount,
      retryDelay
    })

    // Notify status change
    this.notifyStatusChange(QueueStatus.RETRYING, item, { retryCount: item.retryCount, retryDelay })

    // Remove from processing queue temporarily
    const idx = this.processingQueue.indexOf(item)
    if (idx > -1) {
      this.processingQueue.splice(idx, 1)
    }

    // Schedule retry
    const timerId = setTimeout(() => {
      this.retryTimers.delete(item.blockId)
      this.processingQueue.push(item)
      this.executeSave(item)
    }, retryDelay)

    this.retryTimers.set(item.blockId, timerId)
  }

  /**
   * Handle max retries exceeded
   * @private
   */
  handleMaxRetriesExceeded(item, result) {
    console.error('[SaveQueue] Max retries exceeded for block:', item.blockId)

    // Remove from processing queue
    const idx = this.processingQueue.indexOf(item)
    if (idx > -1) {
      this.processingQueue.splice(idx, 1)
    }

    const errorMsg = result?.error || 'Max retries exceeded'

    // Track analytics
    trackEditorEvent(EditorEvents.SAVE_FAILED, {
      blockId: item.blockId,
      source: item.source,
      retryCount: item.retryCount,
      error: errorMsg
    })

    // Resolve waiting promises with failure (P0-4 fix) - use resolve not reject for graceful handling
    this.resolvePendingPromises(item.blockId, false, { error: errorMsg })

    // Notify status change
    this.notifyStatusChange(QueueStatus.FAILED, item, { error: errorMsg })
  }

  /**
   * Handle connection state change
   * @private
   */
  handleConnectionChange(isOnline) {
    logger.log('Connection changed:', isOnline ? 'online' : 'offline')

    if (isOnline) {
      this.isPaused = false
      // Resume processing if there are pending items
      if (this.queue.size > 0) {
        this.processQueue()
      }
    } else {
      this.isPaused = true
      // Mark pending items as offline queued
      for (const item of this.queue.values()) {
        this.notifyStatusChange(QueueStatus.OFFLINE_QUEUED, item)
      }
    }
  }

  /**
   * Resolve lock version from DOM for a block
   * Handles nested data-block-id elements correctly
   * @private
   */
  resolveLockVersion(blockId) {
    // Task 1.5 - Find wrapper element with both data-block-id AND data-lock-version
    const wrapper = document.querySelector(`[data-block-id="${blockId}"][data-lock-version]`)
    return wrapper?.dataset.lockVersion || null
  }

  /**
   * Update DOM lock_version after successful save
   * @private
   */
  updateDomLockVersion(blockId, newLockVersion) {
    // Task 1.6 - Update data-lock-version in DOM
    if (newLockVersion === undefined) return

    const wrapper = document.querySelector(`[data-block-id="${blockId}"][data-lock-version]`)
    if (wrapper) {
      wrapper.dataset.lockVersion = newLockVersion
      logger.log('Updated DOM lock_version for block:', blockId, 'to:', newLockVersion)
    }
  }

  /**
   * Notify all status change listeners
   * MODIFIED: Now iterates over statusListeners Set instead of single callback (Phase 2 Task 0.1.2)
   * @private
   */
  notifyStatusChange(status, item, extra = {}) {
    for (const listener of this.statusListeners) {
      try {
        listener(status, item, extra)
      } catch (error) {
        console.error('[SaveQueue] Status listener error:', error)
      }
    }
  }

  /**
   * ADDED: Subscribe to status change events (Phase 2 Task 0.1.1)
   * Allows multiple subscribers (ToastManager, ChangeIndicator, etc.)
   *
   * @param {Function} callback - (status, item, extra) => void
   * @returns {Function} Unsubscribe function
   */
  subscribeStatus(callback) {
    if (typeof callback !== 'function') {
      console.error('[SaveQueue] subscribeStatus requires a function callback')
      return () => {}
    }

    this.statusListeners.add(callback)
    logger.log('Status listener added, total:', this.statusListeners.size)

    // Return unsubscribe function
    return () => {
      this.statusListeners.delete(callback)
      logger.log('Status listener removed, total:', this.statusListeners.size)
    }
  }

  /**
   * ADDED: Unsubscribe from status change events (Phase 2 Task 0.1.1)
   *
   * @param {Function} callback - The callback to remove
   * @returns {boolean} True if callback was found and removed
   */
  unsubscribeStatus(callback) {
    const removed = this.statusListeners.delete(callback)
    if (removed) {
      logger.log('Status listener removed via unsubscribeStatus, total:', this.statusListeners.size)
    }
    return removed
  }

  /**
   * Get current queue status
   * @returns {Object} Status object with pending, processing counts
   */
  getStatus() {
    return {
      pending: this.queue.size,
      processing: this.processingQueue.length,
      isProcessing: this.isProcessing,
      isPaused: this.isPaused,
      hasUnsavedChanges: this.queue.size > 0 || this.processingQueue.length > 0
    }
  }

  /**
   * Check if there are any pending or processing items
   * @returns {boolean}
   */
  hasPendingChanges() {
    return this.queue.size > 0 || this.processingQueue.length > 0
  }

  /**
   * P0-5 FIX: Get all blocked changes (preserved from 423 responses)
   * @returns {Map<string, QueueItem>} Map of blockId -> blocked item
   */
  getBlockedChanges() {
    return new Map(this.blockedChanges)
  }

  /**
   * P0-5 FIX: Check if there are any blocked changes
   * @returns {boolean}
   */
  hasBlockedChanges() {
    return this.blockedChanges.size > 0
  }

  /**
   * P0-5 FIX: Retry a specific blocked change
   * Re-enqueues the blocked item for another save attempt
   * @param {string} blockId - Block ID to retry
   * @returns {boolean} True if found and re-enqueued
   */
  retryBlockedChange(blockId) {
    const blocked = this.blockedChanges.get(blockId)
    if (!blocked) {
      logger.log('No blocked changes found for:', blockId)
      return false
    }

    // Remove from blocked and re-enqueue
    this.blockedChanges.delete(blockId)
    this.enqueue(blockId, blocked.patches, blocked.expectedLockVersion, blocked.source)
    logger.log('Retrying blocked change for:', blockId)
    return true
  }

  /**
   * P0-5 FIX: Retry all blocked changes
   * Re-enqueues all blocked items for another save attempt
   * @returns {number} Number of items re-enqueued
   */
  retryAllBlockedChanges() {
    const count = this.blockedChanges.size
    if (count === 0) {
      return 0
    }

    for (const [blockId, item] of this.blockedChanges) {
      this.enqueue(blockId, item.patches, item.expectedLockVersion, item.source)
    }
    this.blockedChanges.clear()
    logger.log('Retried', count, 'blocked changes')
    return count
  }

  /**
   * P0-5 FIX: Clear blocked change for a specific block
   * Call this when the user acknowledges/dismisses a blocked change
   * @param {string} blockId - Block ID to clear
   * @returns {boolean} True if found and cleared
   */
  clearBlockedChange(blockId) {
    const deleted = this.blockedChanges.delete(blockId)
    if (deleted) {
      logger.log('Cleared blocked change for:', blockId)
    }
    return deleted
  }

  /**
   * Force immediate processing (bypass coalesce delay)
   */
  flush() {
    // Clear all coalesce timers
    for (const timerId of this.coalesceTimers.values()) {
      clearTimeout(timerId)
    }
    this.coalesceTimers.clear()

    // Process immediately
    this.processQueue()
  }

  /**
   * Clear all pending items and cancel timers
   */
  clear() {
    // Clear coalesce timers
    for (const timerId of this.coalesceTimers.values()) {
      clearTimeout(timerId)
    }
    this.coalesceTimers.clear()

    // Clear retry timers
    for (const timerId of this.retryTimers.values()) {
      clearTimeout(timerId)
    }
    this.retryTimers.clear()

    // Clear queues
    this.queue.clear()
    // Note: Don't clear processingQueue - those are in-flight requests
  }

  /**
   * Phase 10: Handle beforeunload event to warn about unsaved changes
   * @private
   */
  handleBeforeUnload(event) {
    if (this.hasPendingChanges() || this.hasBlockedChanges()) {
      // Standard way to trigger browser's "Leave site?" dialog
      event.preventDefault()
      // Chrome requires returnValue to be set
      event.returnValue = 'You have unsaved changes. Are you sure you want to leave?'
      return event.returnValue
    }
  }

  /**
   * Destroy the queue and clean up
   */
  destroy() {
    this.clear()
    this.processingQueue = []

    // Phase 10: Remove beforeunload listener
    if (this.options.enableBeforeUnload) {
      window.removeEventListener('beforeunload', this.handleBeforeUnload)
    }

    logger.log('Destroyed')
  }
}
